import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Trash2, Printer } from "lucide-react";
import { formatDluo } from "@/components/LabelPreview";

export default function LabelHistory() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (search = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/labels/history", { params: search ? { q: search } : {} });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const search = (e) => {
    e.preventDefault();
    load(q.trim());
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette entrée de l'historique ?")) return;
    try {
      await api.delete(`/labels/history/${id}`);
      toast.success("Supprimé");
      setItems((it) => it.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <DashboardLayout title="Historique des étiquettes">
      <div className="mb-6 flex items-center justify-between gap-4">
        <form onSubmit={search} className="flex gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="history-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher par nom de client…"
              className="rounded-none bg-zinc-900 border-zinc-800 pl-10"
            />
          </div>
          <Button type="submit" data-testid="history-search-btn" className="bg-red-500 hover:bg-red-600 rounded-none">Rechercher</Button>
          {q && (
            <Button type="button" onClick={() => { setQ(""); load(""); }} variant="outline" className="rounded-none border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:text-white">
              Effacer
            </Button>
          )}
        </form>
        <Link to="/generate" className="text-sm text-zinc-400 hover:text-white flex items-center gap-2" data-testid="history-back-generate">
          <Printer className="w-4 h-4" /> Nouvelle étiquette
        </Link>
      </div>

      <div className="border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 border-b border-zinc-800">
            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Liquide</th>
              <th className="px-4 py-3">Taille</th>
              <th className="px-4 py-3">Lot</th>
              <th className="px-4 py-3">DLUO</th>
              <th className="px-4 py-3">Quantité</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-500 text-sm">Chargement…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-500 text-sm">
                {q ? `Aucune étiquette trouvée pour "${q}"` : "Aucune étiquette générée pour l'instant."}
              </td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} data-testid={`history-row-${it.id}`} className="border-b border-zinc-800 hover:bg-zinc-900/60">
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{fmtDate(it.created_at)}</td>
                <td className="px-4 py-3 text-white font-medium">{it.customer_name || <span className="text-zinc-600">—</span>}</td>
                <td className="px-4 py-3 text-zinc-300">{it.brand} — {it.flavor}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{it.bottle_size_ml} ml</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{it.batch_number}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{formatDluo(it.dluo_date)}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">×{it.quantity}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`history-delete-${it.id}`} onClick={() => del(it.id)} className="text-zinc-400 hover:text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}
